/**
 * Timestamp formatting shared by the views.
 *
 * Extracted from Users.tsx when the Live sessions view needed the same
 * behaviour. Kept in one place because the subtleties are easy to get wrong a
 * second time: local-date bucketing (not UTC), an explicit bucket for missing
 * dates rather than silently filing them under today, and a year that only
 * appears once it stops being obvious.
 */

/** Wall-clock seconds → "4m ago". Session timestamps are epoch seconds. */
export function ago(epochSeconds?: number): string {
  if (!epochSeconds) return "—";
  const secs = Math.max(0, Date.now() / 1000 - epochSeconds);
  if (secs < 60) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

/** Same, from an ISO-8601 string. */
export function agoIso(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : ago(d.getTime() / 1000);
}

export function fmtTime(v?: string): string {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString();
}

export function fmtClock(v?: string): string {
  if (!v) return "";
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? v
    : d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** Clock with seconds and milliseconds — a live timeline moves too fast for
 *  minute precision to distinguish a cue from the speech it caused. */
export function fmtClockPrecise(v?: string): string {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const hms = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  return `${hms}.${String(d.getMilliseconds()).padStart(3, "0")}`;
}

/** "22m 14s" between two ISO timestamps; "" if either is missing. */
export function duration(startIso?: string, endIso?: string): string {
  if (!startIso || !endIso) return "";
  const a = new Date(startIso).getTime();
  const b = new Date(endIso).getTime();
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return "";
  const secs = Math.round((b - a) / 1000);
  const mins = Math.floor(secs / 60);
  return mins ? `${mins}m ${secs % 60}s` : `${secs}s`;
}

export const ALL_DATES = "__all__";
export const UNDATED = "__undated__";

/**
 * Day buckets for the date tabs.
 *
 * Grouped by LOCAL date, not the UTC date the timestamp is stored in: a message
 * sent at 6pm Pacific is stored as the next day in UTC, and filing it under
 * tomorrow would make the tabs disagree with the clock times printed beside them.
 *
 * A message whose timestamp is missing or unparseable gets its own bucket rather
 * than being dropped or silently filed under today — a gap in the record is
 * itself worth seeing.
 */
export function dayKey(v?: string): string {
  if (!v) return UNDATED;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return UNDATED;
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10); // YYYY-MM-DD, in the reader's zone
}

/** "7/31", or "7/31/25" once the year stops being obvious. Numeric and locale-
 *  aware, so a reader outside the US sees their own order (31/7). */
export function dayLabel(key: string): string {
  if (key === UNDATED) return "No date";
  const [y, m, d] = key.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const sameYear = y === new Date().getFullYear();
  return date.toLocaleDateString(undefined, {
    month: "numeric",
    day: "numeric",
    ...(sameYear ? {} : { year: "2-digit" }),
  });
}
